import { FC, useState } from 'react';
import { css } from '@emotion/react';
import { OnboardingModulesQueries } from '@frontend/api-onboarding-modules';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Heading, NakedButton, SpinningLoader, Text, useAlert } from '@frontend/design-system';
import { maskEmail } from '../../utils';
import { UserCreationTrackingIds } from './tracking-ids';

interface ResendSignupLinkProps {
  email?: string;
  locationId?: string;
}
export const ResendSignupLink: FC<ResendSignupLinkProps> = ({ email = '', locationId = '' }) => {
  const { t } = useTranslation('userCreate', { keyPrefix: 'signup-resend' });
  const alerts = useAlert();

  const maskedEmail = maskEmail(email);

  const [linkSent, setLinkSent] = useState(false);
  const { mutate: resendUserInvite, isLoading } = OnboardingModulesQueries.useResendUserInviteEmail({
    onSuccess: () => {
      setLinkSent(true);
      alerts.success(t('Link sent successfully.'));
    },
    onError: (error) => {
      alerts.error(t('An error occurred while sending the link. Please try again.'));
      sentry.log({
        category: 'onboarding',
        message: 'Error occured while re-sending the user-invite link',
        data: { error },
      });
    },
  });

  return (
    <div css={containerStyles}>
      <Icon name='unlink-small' size={24} />
      {linkSent ? (
        <>
          <Heading level={2} textAlign='center'>
            {t('A new link was sent to {{email}}', { email: maskedEmail })}
          </Heading>
          <Heading level={3} textAlign='center'>
            {t('This link will expire in 15 minutes. Come back soon!')}
          </Heading>
        </>
      ) : (
        <>
          <Heading level={2}>{t('Uh oh! This link has expired.')}</Heading>
          {!!email && (
            <>
              <Heading level={3} textAlign='center'>
                {t('Click below to send a new link to {{email}}', { email: maskedEmail })}
              </Heading>
              <NakedButton
                css={actionButtonStyles}
                onClick={() =>
                  resendUserInvite({
                    email,
                    locationId,
                  })
                }
                trackingId={UserCreationTrackingIds.resendLink}
                disabled={isLoading}
              >
                <Text weight='bold' size='medium' color='white'>
                  {t('Send now')}
                </Text>
                {isLoading && <SpinningLoader size={'xs'} color='white' />}
              </NakedButton>
            </>
          )}
        </>
      )}
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
});

const actionButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.colors.neutral90,
  padding: theme.spacing(0, 2),
  height: theme.spacing(4),
  borderRadius: theme.borderRadius.small,
  marginTop: theme.spacing(1),
  [':disabled']: {
    opacity: 0.5,
    backgroundColor: theme.colors.neutral30,
    color: theme.colors.neutral60,
  },
});
